// R63 — RUNNING A SEAL, AND THE ONE DROPPED POLL THAT CALLED A LIVE SEAL LOST.
//
// A seal is the most expensive single thing this house makes and the only generation on this site
// that DETACHES: the request returns a jobKey immediately and the creator polls `/api/seals/status`
// for up to five minutes while the pipeline resolves, draws, gates, scribes, describes and saves.
// `src/sealStatus.js` is R18's law about what one poll ANSWER means, and its header states the
// distinction the whole thing turns on:
//
//     "a status the server DELIBERATELY SENT is an ANSWER and must be acted on, while an ABSENT or
//      unparseable payload is TRANSPORT NOISE ... and re-polling is the right thing to do with that"
//
// **THE LOOP HONOURED THAT FOR ONE OF THE TWO SHAPES TRANSPORT NOISE COMES IN AND NOT THE OTHER.**
// A poll whose body would not parse was `.catch(() => ({}))`, which is not a status, which is "wait"
// — correct. A poll whose REQUEST THREW was inside the same outer `try` as everything else, so one
// dropped connection anywhere in a five-minute wait on a phone fell straight out of the loop into:
//
//     catch { setStatus(t(language, "seal.lost")); setRetryable(true); }
//
// **She is told her seal could not be completed while it is still being drawn.** The pipeline is
// detached — it neither knows nor cares that her poll died — so it finishes, saves, and lands on her
// shelf minutes later through the in-room queue strip, under a room that already told her it was
// lost. R18 wrote the law that covers this exactly and the request half of the loop was never
// brought under it. A throw is a WAIT now, bounded by the same deadline that bounds everything else.
//
// **AND THE SERVER SENDS HER NAMES WITH EVERY FAILURE, WHICH REACHED NOBODY.** Two sites in
// `handleSealGenerate` put `names` on a refusal and one of them says why in the file:
//
//     "The names are returned even when the seal is not: they ARE resolved correctly, and the
//      client names them while the artwork is perfected."
//
// `handleSealStatus` re-sends them on the failed branch (`names: entry.result.names || []`). The
// client's failure path read `message` and `retryable` and dropped the list on the floor. That is
// R23/R26's shape — the code states its own law and the other side breaks it with nothing between —
// and R38's — a capability wired to nothing has no error state. On a FAILURE there is no
// description, so those names reach her nowhere else; they are the only evidence she has that the
// paid half of the run (the resolver, the one paid call left on this path) did its work.
//
// `doFetch`, `sleep` and `now` are PARAMETERS (R46), so `scripts/r63-seal-run.mjs` drives every
// answer this pair of routes can give — including a throw at poll three of five — with no browser,
// no session, no database and no seal.

import {
  sealPollOutcome,
  sealFailureMessage,
  sealFailureRetryable,
} from "../sealStatus.js";

// The delivered seal, as the room renders it. Moved here with the laws that shape it, because a
// type belongs with the thing that builds it.
export type SealForm = { script?: string; text?: string };
export type SealName = { roman?: string; native?: string; script?: string; meaning?: string; forms?: SealForm[] };
export type GeneratedSeal = {
  sealId: number | null; title: string; names: SealName[]; description: string; imageUrl: string;
  /** Her warm line when the read-back could not confirm every letter. `""` on a clean seal. */
  flagNote: string;
};

export const SEAL_GENERATE_URL = "/api/seals/generate";
export const sealStatusUrl = (jobKey: string) => `/api/seals/status?key=${encodeURIComponent(jobKey)}`;

// The house's own name for a seal that arrived without one. A PARAMETER with the English default,
// for the same reason `sealFailureMessage`'s fallback is one (14-B7c): the fixture reads the English
// back with one argument, and the room passes her row.
export const SEAL_DEFAULT_TITLE = "Your Seal";

// ---------------------------------------------------------------------------------------------
// THE ANSWERS. Four for the start, two for the wait — argued per route rather than made uniform
// (R60's ruling): the START can refuse four distinguishable ways, and once a job key exists the
// only two outcomes left are a seal and a failure carrying a sentence.
// ---------------------------------------------------------------------------------------------

export type SealStart =
  | { ok: true; kind: "queued"; jobKey: string }
  | { ok: true; kind: "done"; seal: GeneratedSeal }
  | { ok: false; kind: "sign-in" }
  | { ok: false; kind: "refused"; message: string; names: SealName[]; retryable: boolean }
  | { ok: false; kind: "offline" };

export type SealOutcome =
  | { ok: true; seal: GeneratedSeal }
  | { ok: false; message: string; retryable: boolean; names: SealName[] };

type SealJson = {
  ok?: boolean;
  queued?: boolean;
  jobKey?: string;
  sealId?: number | null;
  title?: string;
  names?: unknown;
  description?: string;
  imageUrl?: string;
  message?: string;
  status?: string;
  flagNote?: string;
};

// A payload's `names` is a list or it is nothing. `|| []` on a field is only honest once something
// has already decided the payload was a success — which is what every caller below does first.
export const sealNamesFrom = (payload: unknown): SealName[] => {
  const names = (payload as SealJson | null)?.names;
  return Array.isArray(names) ? (names as SealName[]) : [];
};

// SHAPING A DELIVERED SEAL. Called only behind a guard that has already established `imageUrl` is
// there (R18: "a success with no picture in it is not a success"), so the picture is not defaulted
// here — a blank `src` would draw a broken image where the seal goes.
export const sealResultFrom = (payload: unknown, defaultTitle: string = SEAL_DEFAULT_TITLE): GeneratedSeal => {
  const d = (payload || {}) as SealJson;
  return {
    sealId: d.sealId ?? null,
    title: String(d.title || defaultTitle || SEAL_DEFAULT_TITLE),
    names: sealNamesFrom(d),
    description: String(d.description || ""),
    imageUrl: String(d.imageUrl || ""),
    // **THE FLAG, CARRIED TO THE SCREEN (her order, 2026-08-08).** Stage 5 marks a seal
    // `delivered-with-flag` when the read-back could not confirm every inscription, and that mark used
    // to reach the trail and nothing else — she received a seal reading WOUND.AITH and HEALLTH and was
    // told nothing. An empty string is the honest default: a clean seal says nothing at all rather
    // than an absence the reader has to interpret.
    flagNote: String(d.flagNote || ""),
  };
};

// ---------------------------------------------------------------------------------------------
// THE MEANING BOX, AND T9-2's LAW ONE ROOM OVER: NO BODY, NO BOX.
//
// `description.split(/\n{2,}/)` over an empty string is `[""]`, so an empty description drew one
// empty `<p>` under a heading that says "What your seal means" — W12-5's DATE-OF-BIRTH-over-nothing
// on the most expensive surface in the house. The heading is DERIVED from having something to say.
// ---------------------------------------------------------------------------------------------

export const sealParagraphs = (description: string): string[] =>
  String(description || "")
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);

export const sealHasMeaning = (seal: Pick<GeneratedSeal, "names" | "description"> | null): boolean =>
  !!seal && (seal.names.length > 0 || sealParagraphs(seal.description).length > 0);

// ---------------------------------------------------------------------------------------------
// THE START. `/api/seals/generate` answers SIX ways and five of them are refusals the room has to
// tell apart: 401; 200 `{ ok:false }` three times over (nothing to inscribe, the provider is not
// configured, over capacity), each with its own sentence and the capacity one with her names on it;
// 200 `{ ok:true, queued:true, jobKey }` (the detached path, which is every signed-in member); a
// legacy SYNCHRONOUS 200 carrying the whole seal (the path taken when `ensureUser` comes back
// empty); and 500.
// ---------------------------------------------------------------------------------------------
export async function startSeal(
  body: Record<string, unknown>,
  doFetch: typeof fetch = fetch,
  defaultTitle: string = SEAL_DEFAULT_TITLE,
): Promise<SealStart> {
  try {
    const res = await doFetch(SEAL_GENERATE_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (res.status === 401) { await res.body?.cancel?.().catch(() => {}); return { ok: false, kind: "sign-in" }; }
    const data = (await res.json().catch(() => ({}))) as SealJson;
    if (data?.queued && data?.jobKey) return { ok: true, kind: "queued", jobKey: String(data.jobKey) };
    // The legacy synchronous success. **THE PICTURE IS THE PROOF** — a 200 `{ ok: true }` with no
    // `imageUrl` is not a seal, and it used to paint the reveal with a broken image in it.
    if (res.ok && data?.ok !== false && data?.imageUrl) {
      return { ok: true, kind: "done", seal: sealResultFrom(data, defaultTitle) };
    }
    return {
      ok: false,
      kind: "refused",
      message: String(data?.message || ""),
      names: sealNamesFrom(data),
      retryable: sealFailureRetryable(data),
    };
  } catch {
    return { ok: false, kind: "offline" };
  }
}

// ---------------------------------------------------------------------------------------------
// THE WAIT. R18 decides what each ANSWER means; this decides what a NON-answer means, which is the
// half that was missing. `sleep` and `now` are parameters so a fixture runs five minutes of polling
// in no time at all and can throw on whichever poll it likes.
// ---------------------------------------------------------------------------------------------
export type SealPollOptions = {
  doFetch?: typeof fetch;
  sleep?: (ms: number) => Promise<void>;
  now?: () => number;
  deadlineMs?: number;
  intervalMs?: number;
  defaultTitle?: string;
};

export type SealPollCopy = { lost: string; slow: string };

export async function pollSeal(
  jobKey: string,
  copy: SealPollCopy,
  options: SealPollOptions = {},
): Promise<SealOutcome> {
  const doFetch = options.doFetch || fetch;
  const now = options.now || (() => Date.now());
  const intervalMs = options.intervalMs ?? 3000;
  const sleep = options.sleep || ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const deadline = now() + (options.deadlineMs ?? 5 * 60 * 1000);
  while (now() < deadline) {
    await sleep(intervalMs);
    let payload: unknown = {};
    try {
      const res = await doFetch(sealStatusUrl(jobKey));
      payload = await res.json().catch(() => ({}));
    } catch {
      // **THE FIX.** A dropped request is transport noise, exactly as an unparseable body is, and
      // R18's own header says so. It used to escape the loop and tell her the seal was lost while
      // the detached pipeline was still drawing it.
      payload = {};
    }
    const outcome = sealPollOutcome(payload);
    if (outcome === "ready") {
      return { ok: true, seal: sealResultFrom(payload, options.defaultTitle) };
    }
    if (outcome === "failed") {
      return {
        ok: false,
        message: sealFailureMessage(payload, copy.lost),
        retryable: sealFailureRetryable(payload),
        // The names the server sends with every failure and the room never rendered.
        names: sealNamesFrom(payload),
      };
    }
  }
  // Out of time, not out of luck: the run is detached and the queue strip still delivers it.
  return { ok: false, message: copy.slow, retryable: true, names: [] };
}
